import React, {useContext, useEffect, useMemo, useState} from 'react';
import Form from 'antd/lib/form';
import _ from 'lodash';
import { getOptionsForAttr } from '../fields/coderefs';
import { stringToUIString } from '../../utils/dateUtils';
import { FormEditor } from '../../editor/FormEditor';
import {PortalContext, PRODUCTION} from '../PortalApp';
import memoize from 'fast-memoize';
import { FormValidationContext } from '../../context/FormValidationProvider';
import { traversePresentationMetadata } from '../DynamicComponent';
import {getMappedValue, setMappedValue} from './mappings';
import {Attribute, AttributeCondition} from './api/attribute';
import {getDefaultValue} from './metadata';
import {FieldProps} from './Field';
import {buildFormUIConfig, FormUIConfigContext, NULL_CONFIG} from './formUIContext';

export const FormContext = React.createContext<FormContextType>(null);
export const BindContext = React.createContext({});
import {GlobalStateContext} from '../../context/GlobalStateProvider';
import {coerceToBoolean, coerceToYesNo} from './coercer';

export function MDForm({model, dataPrefill=EMPTY_OBJ, prefix='', productSetupContext = EMPTY_OBJ,
                         presentationMetadata, extendedAttributes, children}) {

  if (prefix && !prefix.endsWith('.')) {
    prefix += '.';
  }
  const { isDesktop, isMobile, isTablet } = useContext(GlobalStateContext);
  const parentUIConfig = useContext(FormUIConfigContext);

  const metadata = useMemo(() => {
    return { ...getAttributeMetadataFromProductSetup(productSetupContext), ...(extendedAttributes||{})};
  }, [productSetupContext, extendedAttributes]);

  let parentContext = useContext(FormContext);

  if (parentContext && parentContext.prefix) {
    prefix = parentContext.prefix + (parentContext.prefix.charAt(parentContext.prefix.length-1) !== '.' ? '.' : '') + prefix;
  }

  const formValidationContext = useContext<any>(FormValidationContext);
  const formContext = createFormContext(model, prefix, metadata, productSetupContext, presentationMetadata, formValidationContext);

  useEffect(() => {

    traversePresentationMetadata(presentationMetadata, (componentId: string, props: FieldProps) => {
      let attrId = props.attr;
      if (componentId === 'Field' && attrId) {
        const attr = metadata[attrId];

        if (attr) {

          const customPrefill = dataPrefill[attrId];
          let value = formContext.getValueByAttr(attrId);
          if (value === undefined) {
            let defaultValue = undefined;
            if (props.defaultValueMapping) {
              defaultValue = getMappedValue(model.data, props.defaultValueMapping);
            } else if (customPrefill) {
              defaultValue = customPrefill(model.data);
            } else if (attr.defaultValue) {
              defaultValue = getDefaultValue(attr);
            }
            if (defaultValue !== undefined) {
              setMappedValue(model.data, prefix+attr.mapping, defaultValue);
              value = defaultValue;
            }
          }

        }
      }
    });

    formContext.validate();

    return () => formValidationContext.removeForm(prefix);
  }, []);

  return <form className='relative-position'>
    {formContext.disabled && <div className='form-disabling-overlay' />}
    <FormContext.Provider value={formContext}>
      <FormUIConfigContext.Provider value={parentUIConfig !== NULL_CONFIG ? parentUIConfig : buildFormUIConfig(isDesktop, isMobile, isTablet)}>
        {!PRODUCTION  && <FormEditor presentationMetadata={presentationMetadata} /> }
        {children}
      </FormUIConfigContext.Provider>
    </FormContext.Provider>
  </form>;

}

const getAttributeMetadataFromProductSetup = memoize(productSetupContext => {
  if (!productSetupContext.attributes||!productSetupContext.attributes.attributes) {
    return {};
  }

  const out = {};
  productSetupContext.attributes.attributes.forEach(attr => {

    const {attributeMappings, label, ...directProps} = attr;
    const apiMapping = attributeMappings.find(am => am.typeCd === 'API');
    if (!apiMapping) {
      return;
    }

    out[attr.id] = {
      label: label || attr.id,
      mapping: apiMapping.mapping,
      ...directProps,
    }
  });
  return out;
});

function getTypeCd(condition) {
  // 'if' is the documented default typeCd for conditions, otherwise return lower-case
  return ((condition.typeCd || 'if').toLowerCase( ));
}

function createFormContext(model, prefix, metadata, productSetupContext, presentationMetadata, validationContext): FormContextType {
  const {setFieldStatuses, formStatuses} = validationContext;
  const getValue = (path, pathPrefix=prefix) => model.getValue(pathPrefix + path);
  const setValue = (path, value) => model.setValue(prefix + path, value);
  const setValues = values => {
    const prefixedValues = {};
    Object.keys(values).forEach(key => {
      prefixedValues[prefix + key] = values[key];
    });
    model.setValues(prefixedValues);
  };

  const getValuesByAttr = (attrIds, mapResult = {}) => {
    return attrIds.reduce((result,currentAttr) => {
      const key = mapResult[currentAttr] || currentAttr;
      return {
        ...result,
        [key]: getValueByAttr(currentAttr)
      }
    },{})
  };

  const getValueByAttr = attrId => {
    const attr = metadata[attrId];
    if(undefined !== attr) {
      const val = getValue(attr.mapping);
      if('Boolean' === attr.dataType && attr.label !== 'Payment Method' ) {
          // return (undefined !== val ? true === val ? 'Yes':'No': undefined);
          return coerceToYesNo(val);
      }
      return val? val : undefined;
    }
    return undefined;
  };

  const setValueByAttr = (attrId, value) => {
    const attr = metadata[attrId];
    if (attr) {
      if (attr.dataType === 'Boolean' && attr.label !== 'Payment Method'){
        value = coerceToBoolean(value);
      }
      //needed to store fields that are represented on UI as number but stored as String
      else if(attr.dataType === 'String' || attr.dataType === 'Limit'){
        value = !!value ? value.toString() : value;
      }
      else if(attr.dataType === 'Integer'){
        value = !!value ? parseInt(value) : 0;
      }
      setValue(attr.mapping, value);
      touch(attrId);
      validate();
    }
  };

  const setValuesByAttr = values => {
    const prefixedValues = {};
    const statuses = {};
    Object.keys(values).forEach(attrId => {
      const attr = metadata[attrId];

      if (attr) {
        const value = values[attrId];
        prefixedValues[prefix + attr.mapping] = value;
        statuses[attrId] = {
          touched: true
        }
      }
    });
    model.setValues(prefixedValues);
    setFieldStatuses(prefix, statuses);
    validate();
  };

  function touch(attrId: string, touched:boolean=true) {

    const statuses = {};

    const attr = metadata[attrId];
    if (attr) {
      statuses[attrId] = {
        touched: touched,
      }
    }
    setFieldStatuses(prefix, statuses);
  }

  const getUIValueByAttr = attrId => {
    const attr: Attribute = metadata[attrId];
    if (!attr) {
      return undefined;
    }

    let val = getValue(attr.mapping);
    if (attr.dataType === 'date' && val) {
      return stringToUIString(val);
    }

    let options = getOptionsForAttr(attr);
    if (options) {
      return options.index[val] || val;
    }

    return val;
  };

  function evalConditions(attributeConditions: AttributeCondition[], matchAll:boolean=true, pathPrefix: string=prefix): boolean {
    let found = false;
    for (let cond of attributeConditions) {
      const condTypeCd = getTypeCd(cond);
      if ((condTypeCd === 'or') || (condTypeCd === 'and')) {
        // Recurse on the children (matchAll = (condTypeCd === 'and'))
        if (cond.attributeCondition) found = evalConditions(cond.attributeCondition, (condTypeCd === 'and'), pathPrefix);
      } else if (condTypeCd === 'not') {
        // determine if children are and'd or or'd together
        const matchAll = (( ! cond.attributeCondition) || (getTypeCd(cond.attributeCondition[0]) !== 'or'));
        // recurse on the children and negate the result
        if (cond.attributeCondition) found = ( ! evalConditions(cond.attributeCondition, matchAll, pathPrefix));
      } else {
        // handle the 'if' directly (should be Equals or NotEquals)
        const parentAttr = metadata[cond.attributeId];
        if (!parentAttr) {
          console.warn(`attribute ${cond.attributeId} doesn't exist, it was referred as an applicability condition`);
          break;  // ???this returns the last known value - why not continue?
        }
        let pval = getValue(parentAttr.mapping, pathPrefix);
        if (pval === true) {
          pval = 'Yes';
        } else if (pval === false) {
          pval = 'No';
        }

        const operator = cond.operator.toLowerCase( );
        found = ((('equals' === operator) && (pval === cond.value)) || (('notequals' === operator) && (pval !== cond.value)));
      }
      if ((matchAll && ( ! found)) || (( ! matchAll) && found)) break;
    }
    return found;
  }

  function validateAttribute(attr: Attribute, value: any): string[] {
    let errors = undefined;

    function addError(msg) {
      errors = errors || [];
      errors.push(msg);
    }

    let applicable = true;
    if (attr.attributeRelationships) {
      for (let attrRel of attr.attributeRelationships) {
        if (attrRel.typeCd === 'ApplicableIf' && attrRel.attributeConditions) {
          if (!evalConditions(attrRel.attributeConditions)) {
            applicable = false;
          }
        }
      }
    }

    const checkValue = value =>{
      if (attr.dataType === 'Address'){
        return (value === undefined || value === null || value.addr1 === '' || value.addr1 === null || value.addr1 === undefined)
      }else {
        return (value === '' || value === null || value === undefined)
      }
    }

    if (coerceToBoolean(attr.requiredInd)  && applicable && checkValue(value)) {
      addError('Please fill out this field');
    }

    return errors;

  }

  function validate() {

    const statuses = {};
    traversePresentationMetadata(presentationMetadata, (componentId: string, props: FieldProps) => {
      let attrId = props.attr;
      if (componentId === 'Field' && attrId) {
        const attr = metadata[attrId];

        if (attr) {
          const touched = formStatuses[prefix] && formStatuses[prefix][attrId] ? formStatuses[prefix][attrId].touched : undefined;
          const value = getValueByAttr(attrId);
          statuses[attrId] = {
            touched: touched === undefined ? false: touched,
            errors: validateAttribute(attr, value)
          }
        }
      }
    });

    setFieldStatuses(prefix, statuses);

  }

  const getStatus = attrId => (formStatuses[prefix]||EMPTY_OBJ)[attrId]||NULL_STATUS;

  const disabled = model.locked;

  return {
    prefix,
    getValue,
    setValues,
    setValue,
    getValueByAttr,
    getValuesByAttr,
    getUIValueByAttr,
    setValueByAttr,
    setValuesByAttr,
    getStatus,
    metadata,
    productSetupContext,
    validate,
    validateAttribute,
    touch,
    evalConditions,
    disabled
  };
}

export interface FieldStatus {
  touched: boolean;
  errors: string[];
}

export interface FormContextType {

  prefix: string;
  metadata: {
    [key: string]: Attribute
  };
  productSetupContext: any;

  getValue(path: string, pathPrefix?: string): any;
  setValue(path: string, value: any): void;
  setValues(values: {
    [key: string]: any
  }): void;

  getValuesByAttr(attrIds: string[], mapResult: {
    [key:string]: string
  }): {
    [key:string]: any
  };

  getValueByAttr(attrId: string): any;

  setValueByAttr(attrId: string, value: any): void;

  setValuesByAttr(values: {
    [attrId: string]: any
  }): void;

  getUIValueByAttr(attrId: string) : any;

  getStatus(attrId: string): FieldStatus;

  evalConditions(attributeConditions: AttributeCondition[], matchAll?: boolean, pathPrefix?: string): boolean;

  validateAttribute(attr: Attribute, value: any): string[];

  touch(attrId: string, touched: boolean): void;

  validate(): void;

  disabled: boolean;

}

const NULL_STATUS = Object.freeze({
  touched: false
});

const EMPTY_OBJ = Object.freeze({});